import React from "react";
import {
  Breadcrumbs as MuiBreadcrumbs,
  Typography,
  Link,
  Box,
  IconButton,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { Link as RouterLink, useLocation, useNavigate } from "react-router-dom";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import { useDispatch, useSelector } from "react-redux";
import { cleardisbursementButtonSlice } from "../../services/redux/slices/disbursementButtonSlice";
import { setQuickHelpInfoRoute } from "../../services/redux/slices/tabsSlice";
import { TruncatedTextWithTooltip } from "./TruncatedTextWithTooltip";

interface BreadcrumbsProps {
  items: {
    label: string;
    path?: string;
  }[];
  onBackClick?: () => void;
}

const Breadcrumbs: React.FC<BreadcrumbsProps> = ({ items, onBackClick }) => {
  const theme = useTheme();
  const isXsOrSm = useMediaQuery(theme.breakpoints.down("md"));
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const quickHelpInfoRouteValue = useSelector(
    (state: any) => state.tabs?.quickHelpInfoRoute
  );

  const handleBackClick = () => {
    dispatch(cleardisbursementButtonSlice());
    if (onBackClick) {
      onBackClick();
    } else if (location?.pathname.includes("term-loan/view-all-loans")) {
      navigate("/dashboard");
    } else if (location?.pathname.includes("term-loan")) {
      if (quickHelpInfoRouteValue === "dashboard") {
        dispatch(setQuickHelpInfoRoute(""));
        navigate("/dashboard");
      } else {
        navigate(-1);
      }
    } else {
      navigate(-1);
    }
  };

  // Filter out any items that are null, undefined, or have an empty label
  const validItems = items?.filter((item) => item && item.label);

  return (
    <Box display="flex" alignItems="center" marginBottom="4px">
      <IconButton
        onClick={handleBackClick}
        size="small"
        sx={{ mr: 1, color: "rgba(0, 179, 181, 1)" }}
      >
        <ArrowBackIosNewIcon
          fontSize="small"
          color="action"
          sx={{ color: "rgba(0, 179, 181, 1)" }}
        />
      </IconButton>

      <MuiBreadcrumbs aria-label="breadcrumb">
        {validItems.map((item, index) => {
          const isLastItem = index === validItems.length - 1;

          if (isLastItem) {
            // Last item is always non-clickable text
            return (
              <Typography key={index} className="breadcrumbs2">
                {item.label}
              </Typography>
            );
          } else if (item.path) {
            // Only render a link if a path is defined
            return (
              <Link
                key={index}
                to={item.path}
                component={RouterLink}
                underline="hover"
                className="breadcrumbs"
              >
                {item.label}
              </Link>
            );
          } else {
            // Handle case where an intermediate item has no path but isn't the last one
            return (
              <TruncatedTextWithTooltip
                key={index}
                className="breadcrumbs"
                text={item.label}
                limit={isXsOrSm ? 15 : 30}
              />
            );
          }
        })}
      </MuiBreadcrumbs>
    </Box>
  );
};

export default Breadcrumbs;
